import { useEffect, useMemo, useState } from 'react';
import ErrorDisplay from '../../../../components/ErrorDisplay';
import { LessonPicker } from '../../../../pickers/lesson/LessonPicker';
import type { LearningLesson } from '../../../Lessons/services/learningTypes';
import type { QuestTemplateFirestore, QuestTemplateLessonFirestore } from '../../services/questTypes';
import { buildResourceUsageMap } from '../../utils/questUsageUtils';
import { QuestFormActions } from './QuestFormActions';
import { QuestFormFields } from './QuestFormFields';
import { QuestFormSectionDivider } from './QuestFormSectionDivider';
import { useQuestForm } from './useQuestForm';

interface LessonSpecificFormData {
    imageUrl: string;
    lessonColor: string;
    sectionId: string;
    lessonId: string;
}

interface LessonQuestFormProps {
    questTemplate?: QuestTemplateLessonFirestore;
    initialOrder: number;
    onSave: (questTemplate: QuestTemplateLessonFirestore) => void;
    onSaveAndContinue?: (questTemplate: QuestTemplateLessonFirestore) => void;
    onCancel: () => void;
    allQuestTemplates: QuestTemplateFirestore[];
}

export const LessonQuestForm = ({ questTemplate, initialOrder, onSave, onSaveAndContinue, onCancel, allQuestTemplates }: LessonQuestFormProps) => {
    const {
        commonFormData,
        loading,
        error,
        handleCommonChange,
        handleSubmit,
        handleSaveAndContinue
    } = useQuestForm({
        questTemplate,
        initialOrder,
        defaultProgressType: 'fixed'
    });

    const [specificFormData, setSpecificFormData] = useState<LessonSpecificFormData>({
        imageUrl: '',
        lessonColor: '#A8E6CF',
        sectionId: '',
        lessonId: '',
    });

    // Build usage map for lessons
    const lessonUsageMap = useMemo(() => {
        return buildResourceUsageMap(allQuestTemplates, 'lessonId');
    }, [allQuestTemplates]);

    useEffect(() => {
        if (questTemplate && questTemplate.id) {
            setSpecificFormData({
                imageUrl: questTemplate.imageUrl,
                lessonColor: questTemplate.lessonColor,
                sectionId: questTemplate.sectionId,
                lessonId: questTemplate.lessonId,
            });
        }
    }, [questTemplate]);

    const handleSpecificChange = (field: string, value: string | boolean) => {
        setSpecificFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleChange = (field: string, value: string | boolean) => {
        if (field in commonFormData) {
            handleCommonChange(field as keyof typeof commonFormData, value as string | boolean);
        } else {
            handleSpecificChange(field, value);
        }
    };

    const validateSpecificFields = (): string | null => {
        if (!specificFormData.imageUrl.trim()) {
            return 'Image URL is required for lesson quests';
        }
        if (!specificFormData.sectionId.trim()) {
            return 'Section ID is required for lesson quests';
        }
        if (!specificFormData.lessonId.trim()) {
            return 'Lesson ID is required for lesson quests';
        }
        return null;
    };

    const createQuestTemplate = (baseData: Partial<QuestTemplateLessonFirestore>): QuestTemplateLessonFirestore => {
        return {
            ...baseData,
            type: 'lesson',
            maxProgress: 1,
            imageUrl: specificFormData.imageUrl,
            lessonColor: specificFormData.lessonColor,
            sectionId: specificFormData.sectionId,
            lessonId: specificFormData.lessonId,
        } as QuestTemplateLessonFirestore;
    };

    const onSubmit = handleSubmit(validateSpecificFields, createQuestTemplate, onSave);
    const onSaveAndContinueClick = onSaveAndContinue ? handleSaveAndContinue(validateSpecificFields, createQuestTemplate, onSaveAndContinue) : undefined;

    return (
        <form onSubmit={onSubmit} className="space-y-4">
            {error && (
                <ErrorDisplay message={error} />
            )}

            <QuestFormFields
                formData={commonFormData}
                questType="lesson"
                disableProgressType={true}
                onChange={handleChange}
            />

            <QuestFormSectionDivider title="Lesson-specific fields" />

            <LessonPicker
                value={specificFormData.lessonId}
                onChange={(sectionId: string, lesson: LearningLesson | null) => {
                    if (lesson) {
                        handleChange('lessonId', lesson?.uid || '');
                        handleChange('imageUrl', lesson.imageSrc);
                        handleChange('lessonColor', lesson.color || '#A8E6CF');
                        handleChange('sectionId', sectionId);
                    } else {
                        handleChange('lessonId', '');
                        handleChange('imageUrl', '');
                        handleChange('lessonColor', '#A8E6CF');
                        handleChange('sectionId', '');
                    }
                }}
                label="Lesson *"
                required
                usageMap={lessonUsageMap}
            />

            {/* Display selected story image (read-only) */}
            {specificFormData.imageUrl && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Selected Lesson Image
                    </label>
                    <div className="grid  border border-gray-300 rounded-md p-4 place-content-center" style={{ backgroundColor: specificFormData.lessonColor }}>
                        <img
                            src={specificFormData.imageUrl}
                            alt={specificFormData.lessonId}
                            className="max-w-full h-auto rounded-md w-32 h-32 object-cover"
                        />
                    </div>
                </div>
            )}

            {error && (
                <ErrorDisplay message={error} />
            )}

            <QuestFormActions
                onCancel={onCancel}
                onSaveAndContinue={onSaveAndContinueClick}
                loading={loading}
                isEditMode={!!questTemplate?.id}
            />
        </form>
    );
};
